import { parseOrThrow } from '@/shared/utils/api/parseResponse';
import { apiClient } from '@/shared/utils/api/apiClient';
import { milestonesSchema, modelMembersSchema, propertiesSchema } from '../api/detailsContracts';
import type { Milestone, Milestones, ModelMembers, Properties, Property } from '../api/detailsContracts';

const project = (projectId: string): string => `/api/v1/projects/${encodeURIComponent(projectId)}`;
const propertiesPath = (projectId: string, modelId?: number): string =>
  modelId === undefined
    ? `${project(projectId)}/properties`
    : `${project(projectId)}/models/${String(modelId)}/properties`;

/** Key-value properties of a project, or of one of its linked models when `modelId` is given. */
export const detailsService = {
  async properties(projectId: string, modelId?: number): Promise<Properties> {
    return parseOrThrow(
      propertiesSchema,
      await apiClient.get<unknown>(propertiesPath(projectId, modelId)),
      'properties'
    );
  },

  async saveProperties(
    projectId: string,
    modelId: number | undefined,
    items: readonly Property[],
    etag: string
  ): Promise<Properties> {
    return parseOrThrow(
      propertiesSchema,
      await apiClient.put<unknown>(
        propertiesPath(projectId, modelId),
        { items },
        { headers: { 'If-Match': etag } }
      ),
      'properties'
    );
  },

  /** The project members assigned to a linked model. */
  async modelMembers(projectId: string, modelId: number): Promise<ModelMembers> {
    return parseOrThrow(
      modelMembersSchema,
      await apiClient.get<unknown>(`${project(projectId)}/models/${String(modelId)}/members`),
      'model members'
    );
  },

  async saveModelMembers(
    projectId: string,
    modelId: number,
    subjects: readonly string[],
    etag: string
  ): Promise<ModelMembers> {
    return parseOrThrow(
      modelMembersSchema,
      await apiClient.put<unknown>(
        `${project(projectId)}/models/${String(modelId)}/members`,
        { subjects },
        { headers: { 'If-Match': etag } }
      ),
      'model members'
    );
  },

  async milestones(projectId: string): Promise<Milestones> {
    return parseOrThrow(
      milestonesSchema,
      await apiClient.get<unknown>(`${project(projectId)}/milestones`),
      'milestones'
    );
  },

  async saveMilestones(
    projectId: string,
    items: readonly Milestone[],
    etag: string
  ): Promise<Milestones> {
    return parseOrThrow(
      milestonesSchema,
      await apiClient.put<unknown>(
        `${project(projectId)}/milestones`,
        { items },
        { headers: { 'If-Match': etag } }
      ),
      'milestones'
    );
  },
};
